import React, { useEffect, useState } from 'react';
import {
  COMMON_CONSTANTS,
  getTranslatedText,
  SaveCard,
  toast,
  useActivityTracker,
  useProduct
} from '@nile/components';
import {
  DisplayText,
  Modal,
  Page,
  RadioButton,
  TextStyle,
  Tippy
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getValFromVisibilityObject,
  getVisibiltyObject
} from '@nile/rmm/src/modules/knowledge-base/helper';
import {
  ARTICLE_VISIBILITY as articleVisibility,
  CREATE_ARTICLE_ID
} from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import RequesterShareFilter from '@nile/rmm/src/modules/knowledge-base/components/RequesterShareFilter';
import TechGroupShareFilter from '@nile/rmm/src/modules/knowledge-base/components/TechGroupShareFilter';
import { cloneDeep, isArray, isEmpty } from 'lodash';

function ArticleVisibility({
  article,
  activator,
  setArticle,
  onArticleSave,
  onVisibilityChange,
  currentUser,
  isSubmit,
  actionName,
  actionIcon
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibility, setVisibility] = useState(article?.visibility);
  const [requireLogin, setRequireLogin] = useState(article?.loginRequired);
  const [visibilityError, setVisibilityError] = useState([]);
  const isITProduct = useProduct(COMMON_CONSTANTS.IT);

  const activatorButton = activator ? (
    <div onClick={() => setOpen((prevState) => !prevState)}>{activator}</div>
  ) : (
    <div
      className="flex-center"
      onClick={() => setOpen((prevState) => !prevState)}
    >
      <SVGIcon name="edit" />
    </div>
  );

  const isDraft = article?.status === 'DRAFT';
  const visibilityOptions = isDraft
    ? articleVisibility?.filter((el) => el?.key === 'TECHNICIAN')
    : articleVisibility;

  const updateShareLink = (val) => {
    const articleProps = {
      loginRequired: val === 'REQUIRED'
    };
    if (val === 'NOT_REQUIRED') {
      const existingRequesterVisibility = visibility?.find(
        (item) => item?.portalType === COMMON_CONSTANTS.REQUESTER
      );
      let updatedVisibility = visibility;
      const allRequesterVisibility = getVisibiltyObject(
        'ALL',
        COMMON_CONSTANTS.REQUESTER
      );
      if (!existingRequesterVisibility) {
        updatedVisibility = [...visibility, allRequesterVisibility];
      } else {
        updatedVisibility = [
          ...visibility?.filter(
            (item) => item?.portalType !== COMMON_CONSTANTS.REQUESTER
          ),
          allRequesterVisibility
        ];
      }
      articleProps.visibility = updatedVisibility;
    }
    setRequireLogin(val === 'REQUIRED');

    if (setArticle) {
      setArticle((prevState) => ({
        ...prevState,
        ...articleProps
      }));
    }
  };

  const handleChoiceChange = (val, key, skipObjectConversion = false) => {
    if (key === 'SHARELINK') {
      updateShareLink(val);
      return;
    }
    let clonedVisibility = cloneDeep(visibility || []);
    let visibilityValue = skipObjectConversion
      ? isArray(val)
        ? { ...val?.[0], data: val }
        : val
      : getVisibiltyObject(val, key);
    const index = clonedVisibility?.findIndex(
      (item) => item?.portalType === key
    );
    visibilityValue = { ...visibilityValue };

    if (visibilityValue?.sharedType === 'ONLY_ME') {
      visibilityValue = {
        ...visibilityValue,
        addedUserIds: [currentUser?.userId],
        users: [
          {
            userId: currentUser?.userId,
            name: currentUser?.name
          }
        ]
      };
    }

    if (index > -1) {
      clonedVisibility[index] =
        val === 'ALL'
          ? getVisibiltyObject(val, key)
          : {
              ...clonedVisibility[index],
              ...visibilityValue
            };
    } else {
      clonedVisibility.push(visibilityValue);
    }

    if (val === 'NONE') {
      clonedVisibility = clonedVisibility?.filter(
        (el) => el?.portalType !== key || el?.sharedType === val
      );
    }
    setVisibility(clonedVisibility);
  };

  const changeVisibility = async () => {
    setVisibilityError([]);
    let updatedVisibility = visibility?.filter((item) => {
      return item?.sharedType !== 'NONE';
    });
    updatedVisibility = updatedVisibility
      ?.map((item) => {
        return item?.data ? item?.data : item;
      })
      .flat();

    let error = false;
    updatedVisibility?.map((item, index) => {
      if (item?.sharedType === 'SPECIFIC') {
        if (item?.portalType === COMMON_CONSTANTS.REQUESTER) {
          const emptyCondition =
            isEmpty(item?.client?.accountId) &&
            item?.clientSharedType !== 'AllClients';
          if (item?.userRoleSharedType !== 'AllRoles' && isEmpty(item?.roles)) {
            setVisibilityError((prevState) => [
              ...prevState,
              {
                portalType: item?.portalType,
                index,
                error: true,
                sharedType: item?.sharedType
              }
            ]);
            error = true;
          }
        }
        if (item?.portalType === COMMON_CONSTANTS.TECHNICIAN) {
          if (isEmpty(item?.groups)) {
            setVisibilityError((prevState) => [
              ...prevState,
              {
                portalType: item?.portalType,
                index,
                error: true,
                sharedType: item?.sharedType
              }
            ]);
            error = true;
          }
        }
      }
    });

    if (error) {
      toast('error', 'rmm.schedule.fill.required.fields');
      return;
    }
    setLoading(true);

    let artprops = { status: article?.status, loginRequired: requireLogin };
    if (!isEmpty(updatedVisibility)) {
      artprops = { ...artprops, visibility: updatedVisibility };
    }
    if (isDraft) {
      artprops = { ...artprops, loginRequired: true };
    }
    if (onVisibilityChange) {
      onVisibilityChange({
        ...artprops
      });
      setOpen(false);
      return;
    }

    if (
      (article?.itemId && article?.itemId !== CREATE_ARTICLE_ID) ||
      isSubmit
    ) {
      useActivityTracker({
        event: 'KB_ARTICLE_VISIBILITY_CHANGE',
        data: {
          visibility: artprops?.visibility
        }
      });

      await onArticleSave({
        articleProps: {
          ...artprops
        }
      });
      setOpen(false);
      setLoading(false);
    } else {
      await setArticle((prev) => ({
        ...prev,
        ...artprops
      }));
      setOpen(false);
      setLoading(false);
    }
  };

  const renderSpecific = (portalType, selectedType) => {
    let artVisibility = article?.visibility?.map((item) => {
      let dta = { ...item };
      if (isITProduct) {
        dta = { ...dta, clientSharedType: 'AllClients' };
      } else {
        dta = { ...dta, siteSharedType: 'AllSites' };
      }
      return {
        ...item,
        sharedType: getValFromVisibilityObject(dta)?.val
      };
    });
    artVisibility = artVisibility?.filter(
      (item) => item?.portalType === portalType
    );

    const requesterVisibility = visibility?.filter(
      (item) => item?.portalType === portalType
    );

    const disabled = isEmpty(requesterVisibility)
      ? true
      : requesterVisibility?.[0]?.sharedType !== selectedType;

    if (portalType === COMMON_CONSTANTS.REQUESTER) {
      return (
        <div className="ml20">
          <RequesterShareFilter
            data={disabled ? [] : requesterVisibility}
            mappingKey={portalType}
            disabled={disabled}
            updateData={handleChoiceChange}
            hideSite
            hideAllClients
          />
        </div>
      );
    } else {
      return (
        <div className="ml-30">
          <TechGroupShareFilter
            data={disabled ? [] : requesterVisibility}
            originalData={artVisibility}
            mappingKey={portalType}
            disabled={disabled}
            updateData={handleChoiceChange}
          />
        </div>
      );
    }
  };

  const getDefaultVisibility = () => {
    const requesterVisibility =
      article?.status !== 'DRAFT' &&
      getVisibiltyObject('ALL', COMMON_CONSTANTS.REQUESTER);
    const techGroupVisibility =
      article?.status !== 'DRAFT'
        ? getVisibiltyObject('ALL', COMMON_CONSTANTS.TECHNICIAN)
        : {
            val: 'ONLY_ME',
            sharedType: 'ONLY_ME',
            addedUserIds: [currentUser?.userId],
            portalType: COMMON_CONSTANTS.TECHNICIAN,
            userSharedType: 'User',
            groupSharedType: 'AllGroups',
            users: [
              {
                userId: currentUser?.userId,
                name: currentUser?.name
              }
            ]
          };
    let defaultVisibility = [requesterVisibility, techGroupVisibility];
    defaultVisibility = defaultVisibility.filter((el) => el);
    return defaultVisibility;
  };

  useEffect(() => {
    setVisibilityError([]);
    const { visibility, orgStatus, status } = article || {};
    if (!isEmpty(visibility) && (orgStatus ? orgStatus === status : true)) {
      let visible = visibility?.map((itm) => {
        let dta = itm;
        if (isITProduct) {
          dta = { ...dta, clientSharedType: 'AllClients' };
        } else {
          dta = { ...dta, siteSharedType: 'AllSites' };
        }
        return {
          ...itm,
          sharedType: itm?.sharedType || getValFromVisibilityObject(dta)?.val
        };
      });
      const hasRequesterValue = visible?.find(
        (item) => item?.portalType === COMMON_CONSTANTS.REQUESTER
      );
      if (isEmpty(hasRequesterValue)) {
        visible = [
          ...visible,
          getVisibiltyObject('NONE', COMMON_CONSTANTS.REQUESTER)
        ];
      }
      setVisibility(visible);
    } else {
      const defaultVisibility = getDefaultVisibility();
      setVisibility(defaultVisibility);
    }
  }, [article?.visibility, open]);

  useEffect(() => {
    setRequireLogin(article?.loginRequired);
  }, [article?.loginRequired]);

  return (
    <Modal
      activator={activatorButton}
      open={open}
      onClose={() => setOpen((prevState) => !prevState)}
      showCloseButton={false}
      medium
    >
      {open ? (
        <Page
          contentHeight="100px"
          title={getTranslatedText(intl, 'common.app.kb.article.access.title')}
          variant="secondary"
          divider
          primaryAction={
            <div className="flex-center">
              <SaveCard
                isNewLayout
                loading={loading}
                actionIcon={actionIcon || 'SendWhite'}
                handleSave={changeVisibility}
                actionName={
                  actionName ||
                  getTranslatedText(intl, 'admin.customerSatisfaction.publish')
                }
                actions={{
                  back: () => {
                    setOpen(false);
                    setVisibility([]);
                  }
                }}
              />
            </div>
          }
        >
          <div className="flex fcol scroll pt20 pl40 pr40">
            {visibilityOptions.map((item) => (
              <div className="flex fcol g5 mb-30" key={item.key}>
                <div className="flex">
                  <div className="flex f11 fcol g5">
                    <DisplayText element="h3" weight="SemiBold">
                      <FormattedMessage
                        id={item?.label || 'app.noData.message'}
                      />
                    </DisplayText>
                    <TextStyle variation="subdued">
                      <FormattedMessage
                        id={item?.description || 'app.noData.message'}
                      />
                    </TextStyle>
                  </div>
                  {item?.tippyContent && (
                    <div className="flex jend actr">
                      <Tippy
                        title={
                          getTranslatedText(intl, item?.tippyContent) ||
                          item?.tippyContent
                        }
                        duration={250}
                        delay={100}
                        placement="bottom"
                        arrow={false}
                        theme="dark"
                        appendTo={document.body}
                      >
                        <div className="flex-center">
                          <SVGIcon name="info" size="sm" />
                        </div>
                      </Tippy>
                    </div>
                  )}
                </div>
                {item?.options?.map((el) => {
                  const currentObject =
                    item?.key === 'SHARELINK'
                      ? {
                          sharedType: requireLogin ? 'REQUIRED' : 'NOT_REQUIRED'
                        }
                      : visibility?.find((el) => el?.portalType === item?.key);
                  const hasError = visibilityError?.find(
                    (err) =>
                      err?.portalType === item?.key &&
                      err?.sharedType === el?.key
                  );
                  const show = el?.status
                    ? el?.status === article?.status
                    : true;

                  return !show ? (
                    <></>
                  ) : (
                    <div className="flex fcol g5">
                      <div className="flex actr g5">
                        <RadioButton
                          disabled={
                            !requireLogin &&
                            item?.key !== 'SHARELINK' &&
                            el?.key !== 'ALL' &&
                            item?.key === 'REQUESTER' &&
                            !isDraft
                          }
                          checked={currentObject?.sharedType === el?.key}
                          label={
                            <div className="flex fcol g5">
                              <TextStyle weight="Medium">
                                <FormattedMessage id={el?.label} />
                              </TextStyle>
                            </div>
                          }
                          onChange={(val) =>
                            handleChoiceChange(el?.key, item?.key)
                          }
                        />
                        {hasError && (
                          <DisplayText>
                            <TextStyle variation="negative">
                              <FormattedMessage id="admin.policyManagement.alertPolicy.customValueCantBeEmpty.errMsg" />
                            </TextStyle>
                          </DisplayText>
                        )}
                      </div>
                      {el?.key === 'SPECIFIC' &&
                        currentObject?.sharedType === 'SPECIFIC' && (
                          <>{renderSpecific(item?.key, el?.key)}</>
                        )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Page>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export default ArticleVisibility;
